import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { ObraActions } from 'Store/Obra/Obra.actions';
import { TenantActions } from 'Store/Tenant/Tenant.actions';
import { Mixpanel } from 'Utils/MixPanel';
import { AuthUtils } from 'Utils/AuthUtils';
import { useFeatureFlag } from 'Hooks/useFeatureFlag';
import AppStorage from 'Utils/AppStorage';
import { getCurrentTenant } from 'Store/Tenant/Tenant.selector';
import { IObraResponse } from 'Data/interfaces/Obra/IObraListResponse';
import { IUserInforResponse } from 'Data/interfaces/Auth/IUserInfoResponse';
import { IUsersConstructionSiteResponse } from 'Data/interfaces/User/IUsersConstructionSiteResponse';
import { IUserLimitsActive } from 'Store/Auth/interfaces/IAuthState';

export const SEARCH_LOCAL_STORAGE_KEY = `CONSTRUCTION_SITE_SEARCH`;

interface IUserObra {
  isLoading: boolean;
  ObrasList?: IObraResponse[];
  userInfo?: IUserInforResponse;
  isLoadingGetUsersConstruction: boolean;
  Users: IUsersConstructionSiteResponse;
  userLimitsActive?: IUserLimitsActive;
  clearConstructionSiteSearch: boolean;
}

const useObra = ({
  isLoading,
  ObrasList,
  userInfo,
  isLoadingGetUsersConstruction,
  Users,
  userLimitsActive,
  clearConstructionSiteSearch,
}: IUserObra) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentTenant = useSelector(getCurrentTenant);

  const whatsappContactLinkFlag = useFeatureFlag('whatsapp-contact-link');
  const whatsappContactLink = (whatsappContactLinkFlag.enabled && typeof whatsappContactLinkFlag.value === 'string')
    ? whatsappContactLinkFlag.value
    : (process.env.REACT_APP_CONTACT_LINK || '');

  const firstOpen = useRef(0);
  const currentSearchOnStorage = AppStorage.GetItem(SEARCH_LOCAL_STORAGE_KEY, 'local') || undefined;
  const [usuariosEPermissoesVisible, setUsuariosEPermissoesVisible] = useState(false);
  const [cadastroObraVisible, setCadastroObraVisible] = useState(false);
  const [isEditObra, setIsEditObra] = useState(false);
  const [conexaoServicoVisible, setConexaoServicoVisible] = useState(false);
  const [csIdConexaoServico, setCsIdConexaoServico] = useState<number>();
  const [modalContaUpgradeVisible, setModalContaUpgradeVisible] = useState(false);
  const [modalUsuarioUpgradeVisible, setModalUsuarioUpgradeVisible] = useState(false);
  const [maxObras, setMaxObras] = useState<number>();
  const [maxUsers, setMaxUsers] = useState<number>();
  const [constructionSiteSearch, setConstructionSiteSearch] = useState<string | undefined>(currentSearchOnStorage);
  const [isSearchSubmited, setIsSearchSubmited] = useState(!!currentSearchOnStorage);
  const obras = useMemo(() => ObrasList?.filter((obra) => !obra.isArchived), [ObrasList]);
  const obrasArquivadas = useMemo(() => ObrasList?.filter((obra) => obra.isArchived), [ObrasList]);
  const obrasForShow = !!obras && obras.length > 0;
  const filteredObras = useMemo(() => {
    return obras?.filter((x) => {
      return !constructionSiteSearch ||
        x.Name.toLocaleLowerCase().indexOf(constructionSiteSearch.toLocaleLowerCase()) > -1;
    });
  }, [obras, constructionSiteSearch]);
  const userId = AuthUtils.getLoggedUser()?.userId;

  const openConexaoServico = (csId: number) => {
    setConexaoServicoVisible(true);
    setCsIdConexaoServico(csId);
  };

  const openUsuariosEPermissoes = (constructionSiteId: number) => {
    if (!isLoading) {
      if (obras) {
        if (maxUsers) {
          if (userLimitsActive?.QtyActiveUsers && (userLimitsActive?.QtyActiveUsers >= maxUsers)) {
            setModalUsuarioUpgradeVisible(true);
          } else {
            setUsuariosEPermissoesVisible(true);
            dispatch(TenantActions.getTenantListUsersConstruction());
            dispatch(ObraActions.getUsersConstructionSite(constructionSiteId));
          }
        } else {
          setUsuariosEPermissoesVisible(true);
          dispatch(TenantActions.getTenantListUsersConstruction());
          dispatch(ObraActions.getUsersConstructionSite(constructionSiteId));
        }
      }
    }
  };

  const openCadastroObra = () => {
    if (!isLoading) {
      if ((maxObras && userLimitsActive?.QtyActiveConstructionSites)
        && (userLimitsActive?.QtyActiveConstructionSites >= maxObras)) {
        setModalContaUpgradeVisible(true);
      } else {
        setIsEditObra(false);
        dispatch(ObraActions.resetState());
        dispatch(TenantActions.getUsers());
        setCadastroObraVisible(true);
      }
    }
  };

  const closeModalContaUpgrade = () => {
    setModalContaUpgradeVisible(false);
  };

  const upgradePlanRedirect = (role: number, planOrder: number) => {
    Mixpanel.track({
      name: 'CALL_T0_ACTION',
      props: {
        origin: 'obras',
        originPath: window.location.pathname,
      },
      userInfo,
      currentListTenant: currentTenant,
    });

    closeModalContaUpgrade();
    if (role === 1) {
      if (planOrder === 4) {
        window.open(whatsappContactLink);
      } else {
        navigate('/faturamento/planos');
      }

    } else {
      window.open('https://bim.maletadoengenheiro.com.br/seja-premium');
    }
  };

  const removeSearch = () => {
    setConstructionSiteSearch(undefined);
    setIsSearchSubmited(false);
    AppStorage.RemoveItem(SEARCH_LOCAL_STORAGE_KEY, 'local');
  };

  useEffect(() => {
    if (firstOpen.current === 0 && userInfo?.Id && userId) {
      Mixpanel.track({
        name: 'PAGE_VIEW',
        props: {},
        userInfo,
        currentListTenant: currentTenant,
      });
      firstOpen.current++;
    }
  }, [userInfo, userId, currentTenant]);

  useEffect(() => {
    if (!isLoadingGetUsersConstruction) {
      dispatch(ObraActions.usersForLimitInvited(Users?.InvitedUsers));
    }
  }, [isLoadingGetUsersConstruction]);

  useEffect(() => {
    if (userInfo?.UserStoreItem.MaxQtyConstructionSites || userInfo?.UserStoreItem.StorePlan?.MaxQtyConstructionSites) {
      setMaxObras(
        userInfo.UserStoreItem.MaxQtyConstructionSites || userInfo?.UserStoreItem.StorePlan?.MaxQtyConstructionSites
      );
    }
    if (userInfo?.UserStoreItem.MaxQtyUsers || userInfo?.UserStoreItem.StorePlan?.MaxQtyUsers) {
      setMaxUsers(userInfo?.UserStoreItem.MaxQtyUsers || userInfo?.UserStoreItem.StorePlan?.MaxQtyUsers);
    }
  }, [userInfo]);

  useEffect(() => {
    if (clearConstructionSiteSearch) {
      removeSearch();
      dispatch(ObraActions.clearConstructionSiteSearch(false));
    }
  }, [clearConstructionSiteSearch]);

  return {
    usuariosEPermissoesVisible,
    cadastroObraVisible,
    isEditObra,
    conexaoServicoVisible,
    csIdConexaoServico,
    modalContaUpgradeVisible,
    modalUsuarioUpgradeVisible,
    isSearchSubmited,
    obrasArquivadas,
    obrasForShow,
    filteredObras,
    constructionSiteSearch,
    obras,
    maxObras,
    maxUsers,
    setConstructionSiteSearch,
    removeSearch,
    setIsSearchSubmited,
    setModalContaUpgradeVisible,
    setCadastroObraVisible,
    setUsuariosEPermissoesVisible,
    setConexaoServicoVisible,
    closeModalContaUpgrade,
    setModalUsuarioUpgradeVisible,
    openConexaoServico,
    openUsuariosEPermissoes,
    openCadastroObra,
    upgradePlanRedirect,
  };
};

export default useObra;
